import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
/**
 * Service for displaying translated toast messages using PrimeNG MessageService
 */
export class ToastService {
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);

  /**
   * Adds a translated toast message with specified severity
   * @param severity - The severity level of the toast message
   * @param summaryKey - Translation key for the main message text
   * @param detailKey - Optional translation key for detailed message text
   * @param life
   * @param key - Optional key of the p-toast that should display the message
   */
  private addToast(
    severity: 'success' | 'info' | 'warn' | 'error',
    summaryKey: string,
    detailKey?: string,
    life: number = 3000,
    key?: string
  ) {
    this.messageService.add({
      severity,
      summary: this.translate.instant(summaryKey),
      detail: detailKey ? this.translate.instant(detailKey) : undefined,
      life,
      key
    });
  }

  /**
   * Displays a success toast message
   * @param summaryKey - Translation key for the main message text
   * @param detailKey - Optional translation key for detailed message text
   * @param life
   */
  success(summaryKey: string, detailKey?: string, life: number = 3000) {
    this.addToast('success', summaryKey, detailKey, life);
  }

  /**
   * Displays an info toast message
   * @param summaryKey - Translation key for the main message text
   * @param detailKey - Optional translation key for detailed message text
   */
  info(summaryKey: string, detailKey?: string) {
    this.addToast('info', summaryKey, detailKey);
  }

  /**
   * Displays a warning toast message
   * @param summaryKey - Translation key for the main message text
   * @param detailKey - Optional translation key for detailed message text
   * @param life
   */
  warn(summaryKey: string, detailKey?: string, life: number = 3000) {
    this.addToast('warn', summaryKey, detailKey, life);
  }

  /**
   * Displays an error toast message
   * @param summaryKey - Translation key for the main message text
   * @param detailKey - Optional translation key for detailed message text
   * @param life - Optional duration in milliseconds (default: 3000)
   * @param key - Optional key of the p-toast that should display the message
   */
  error(summaryKey: string, detailKey?: string, life: number = 3000, key?: string) {
    this.addToast('error', summaryKey, detailKey, life, key);
  }
}
